/**
 * TankInfo Mapper
 *
 * API Response → Domain Model 변환
 */

import type { TankInformationResponse } from '../apiResponse/tankInfoSettingTypes'
import type {
  TankInfo,
  TankInfoCardValues,
  SubSpecItem,
  TankInfoCardName,
} from '../domain/tankInfo'

// ============================================
// API → Domain Mappers
// ============================================

/**
 * API Response를 TankInfo Domain 모델로 변환
 */
export function toDomainTankInfo(apiResponse: TankInformationResponse): TankInfo {
  const tankCard = apiResponse.tank_information?.tank_card
  const waterCard = apiResponse.tank_information?.water_card
  const schoolInfo = apiResponse.school_information

  return {
    tank: {
      volume: tankCard?.volume ?? 0,
      radius: tankCard?.radius ?? 0,
      depth: tankCard?.depth ?? 0,
      memo: tankCard?.memo ?? '',
    },
    water: {
      nh3: waterCard?.nh3 ?? 0,
      no2: waterCard?.no2 ?? 0,
      ph: waterCard?.ph ?? 0,
      dissolvedOxygen: waterCard?.dissolved_oxygen ?? 0,
      temperature: waterCard?.temperature ?? 0,
    },
    school: schoolInfo
      ? {
          count: {
            stockCount: schoolInfo.count_card?.stock_count ?? 0,
            mortalityCount: schoolInfo.count_card?.mortality_count ?? 0,
            harvestCount: schoolInfo.count_card?.harvest_count ?? 0,
            finalCount: schoolInfo.count_card?.final_count ?? 0,
          },
          sampleWeightHistory: schoolInfo.sample_weight_card ?? [],
          averageWeightHistory: schoolInfo.average_weight_card ?? [],
          biomassHistory: schoolInfo.total_biomass_card ?? [], // 필드명 변경: bimass_card → total_biomass_card
          kFactorHistory: schoolInfo.k_factor_card ?? [],
          feedingAmountHistory: schoolInfo.feeding_amount_card ?? [], // 신규 필드
        }
      : null,
  }
}

// ============================================
// Domain → Card Values (계산값 포함)
// ============================================

/**
 * TankInfo Domain을 카드 표시용 값으로 변환
 */
export function toCardValues(tankInfo: TankInfo | null): TankInfoCardValues {
  if (!tankInfo) {
    return createEmptyCardValues()
  }

  const { tank, water, school } = tankInfo

  // 히스토리 배열에서 최신값 추출
  const latestSampleWeight = getLatestValue(school?.sampleWeightHistory)
  const latestAverageWeight = getLatestValue(school?.averageWeightHistory)
  const latestBiomass = getLatestValue(school?.biomassHistory)
  const latestKFactor = getLatestValue(school?.kFactorHistory)
  const latestFeedingAmount = getLatestValue(school?.feedingAmountHistory)

  // Stocking Density 계산: biomass / volume
  const stockingDensity =
    latestBiomass !== undefined && tank.volume > 0
      ? latestBiomass / tank.volume
      : undefined

  return {
    // Tank 카드
    tankVolume: tank.volume,
    tankRadius: tank.radius,
    tankDepth: tank.depth,
    memo: tank.memo,

    // Water 센서 카드
    nh3: water.nh3,
    no2: water.no2,
    ph: water.ph,
    dissolvedOxygen: water.dissolvedOxygen,
    temperature: water.temperature,

    // Fish 카드
    finalCount: school?.count.finalCount,
    stockCount: school?.count.stockCount,
    mortalityCount: school?.count.mortalityCount,
    harvestCount: school?.count.harvestCount,
    sampleWeight: latestSampleWeight,
    averageWeight: latestAverageWeight,
    biomass: latestBiomass,
    kFactor: latestKFactor,
    feedingAmount: latestFeedingAmount,

    // 계산값
    stockingDensity,
  }
}

/**
 * 빈 카드 값 생성
 */
function createEmptyCardValues(): TankInfoCardValues {
  return {
    tankVolume: undefined,
    tankRadius: undefined,
    tankDepth: undefined,
    memo: '',
    nh3: undefined,
    no2: undefined,
    ph: undefined,
    dissolvedOxygen: undefined,
    temperature: undefined,
    finalCount: undefined,
    stockCount: undefined,
    mortalityCount: undefined,
    harvestCount: undefined,
    sampleWeight: undefined,
    averageWeight: undefined,
    biomass: undefined,
    kFactor: undefined,
    feedingAmount: undefined,
    stockingDensity: undefined,
  }
}

/**
 * 배열에서 최신값 (마지막 요소) 추출
 */
function getLatestValue(arr: number[] | undefined): number | undefined {
  if (!arr || arr.length === 0) return undefined
  return arr[arr.length - 1]
}

// ============================================
// Card Value Getters (레거시 호환)
// ============================================

/**
 * 카드 이름으로 값 조회
 */
export function getCardValue(
  cardValues: TankInfoCardValues,
  cardName: TankInfoCardName
): number | undefined {
  switch (cardName) {
    case 'tankVolume':
      return cardValues.tankVolume
    case 'nh3':
      return cardValues.nh3
    case 'no2':
      return cardValues.no2
    case 'ph':
      return cardValues.ph
    case 'do':
      return cardValues.dissolvedOxygen
    case 'temperature':
      return cardValues.temperature
    case 'count':
      return cardValues.finalCount
    case 'sampleWeight':
      return cardValues.sampleWeight
    case 'averageWeight':
      return cardValues.averageWeight
    case 'biomass':
      return cardValues.biomass
    case 'kFactor':
      return cardValues.kFactor
    case 'stockingDensity':
      return cardValues.stockingDensity
    case 'feeding':
      return cardValues.feedingAmount
    case 'memo':
      return undefined // memo는 string이므로 별도 처리
    default:
      return undefined
  }
}

/**
 * Count 카드용 Sub-Spec 데이터 조회
 */
export function getSubSpecs(
  cardValues: TankInfoCardValues,
  cardName: TankInfoCardName
): SubSpecItem[] | undefined {
  if (cardName !== 'count') return undefined

  if (
    cardValues.stockCount === undefined &&
    cardValues.mortalityCount === undefined &&
    cardValues.harvestCount === undefined
  ) {
    return undefined
  }

  return [
    { specName: 'STOCK', curValue: cardValues.stockCount ?? 0 },
    { specName: 'MORTALITY', curValue: cardValues.mortalityCount ?? 0 },
    { specName: 'HARVEST', curValue: cardValues.harvestCount ?? 0 },
  ]
}

/**
 * Tank 물리적 데이터 조회
 */
export function getTankPhysical(
  cardValues: TankInfoCardValues
): { volume: number | undefined; radius: number | undefined; depth: number | undefined } {
  return {
    volume: cardValues.tankVolume,
    radius: cardValues.tankRadius,
    depth: cardValues.tankDepth,
  }
}
